import formatPercent from "../../../hooks/formatPercent";
import { useState } from "react";
import { FaMinus, FaPlus, FaTag } from "react-icons/fa";
import { CircularProgress, Skeleton } from "@mui/material";
import SuccessAddCartPopup from "./SuccessAddCartPopup";
import ErrorAddCartPopup from "./ErrorAddCartPopup";
import ErrorPopup from "../../../common/particles.jsx/ErrorPopup";
import { useAddToCartPbf } from "../../../hooks/purchaseHook/addToCartPbf";
import { UseCartQueryPbf } from "../../../hooks/purchaseHook/useCartQueryPbf";
import PbfRefusalDialog from "./PbfRefusalDialog";
import {
  isPbfRefusal,
  quotaCapHint,
  quotaListLabel,
  stepperMax,
} from "./pbfInventoryText";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const SPECIAL_LABEL_STYLE = {
  MEDICAL_TOOL: "bg-[#F9EBFE] text-[#AA31F6]",
  OOT: "bg-[#FFE7E7] text-[#C62828]",
  PRECURSOR: "bg-[#FFE4E4] text-[#FF0000]",
  PSYCHOTROPIC: "bg-[#FEEBF7] text-[#F6317D]",
  GENERAL: "bg-[#EBF5FE] text-[#3194F6]",
  NARCOTIC: "bg-[#FEF0E6] text-[#F68031]",
};

const getSpecialLabelKey = (label = "") => {
  const value = label.toLowerCase();

  if (value.includes("alat kesehatan")) return "MEDICAL_TOOL";
  if (value.includes("obat obat tertentu (oot)") || value === "oot")
    return "OOT";
  if (value.includes("prekursor")) return "PRECURSOR";
  if (value.includes("psikotropika")) return "PSYCHOTROPIC";
  if (value.includes("narkotika")) return "NARCOTIC";
  if (value === "keras") return "OOT";
  return "GENERAL";
};

// Tier rules match the Android card and checkout: the one tier whose qty
// range covers the qty applies to every unit; max_qty null means "and above".
const coversQty = (tier, qty) =>
  qty >= tier.min_qty && (tier.max_qty == null || qty <= tier.max_qty);

export const getAppliedTier = (discounts, qty) =>
  [...(discounts || [])]
    .filter((tier) => tier.percent > 0)
    .sort((a, b) => b.min_qty - a.min_qty)
    .find((tier) => coversQty(tier, qty)) || null;

const getPercentBadge = (discounts, applied) => {
  if (applied) return formatPercent(applied.percent);
  const percents = (discounts || [])
    .map((tier) => tier.percent)
    .filter((percent) => percent > 0);
  if (!percents.length) return null;
  const min = Math.min(...percents);
  const max = Math.max(...percents);
  return min === max
    ? formatPercent(min)
    : `${formatPercent(min)} - ${formatPercent(max)}`;
};

// The tier the buyer should look at: the applied one, else the next one to
// unlock, else the last one once qty is past all of them.
const getRelatedTier = (discounts, qty, applied) => {
  if (applied) return applied;
  const sorted = [...(discounts || [])]
    .filter((tier) => tier.percent > 0)
    .sort((a, b) => a.min_qty - b.min_qty);
  return sorted.find((tier) => tier.min_qty > qty) || sorted.at(-1) || null;
};

const getTierBarLabel = (tier) =>
  `Beli ${tier.max_qty == null ? `${tier.min_qty}+` : `${tier.min_qty}-${tier.max_qty}`} Disc.${formatPercent(tier.percent)}`;

// qty 0: the range the tiers can bring the price down to ("Rp 95.000 -
// Rp 98.000"), or one price when every tier is equal. A qty in a tier: that
// tier's price. A qty no tier covers: the normal price.
const getPriceLabel = (basePrice, discounts, applied, qty) => {
  if (applied) return toIDR(basePrice * ((100 - applied.percent) / 100));
  if (qty > 0) return toIDR(basePrice);
  const percents = (discounts || [])
    .map((tier) => tier.percent)
    .filter((percent) => percent > 0);
  if (!percents.length) return toIDR(basePrice);
  const lowest = toIDR(basePrice * ((100 - Math.max(...percents)) / 100));
  const highest = toIDR(basePrice * ((100 - Math.min(...percents)) / 100));
  return lowest === highest ? lowest : `${lowest} - ${highest}`;
};

const CardProduct = ({ product, type, isLoadingCard }) => {
  const [orderQty, setOrderQty] = useState(0);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refusal, setRefusal] = useState(null);

  const { isFetching: isFetchingCart } = UseCartQueryPbf();

  const addToCart = useAddToCartPbf();
  const quota = product?.quota;
  const maxQty = stepperMax(product?.stock, quota);
  const quotaEmpty = quota?.remaining === 0;
  const appliedTier = getAppliedTier(product?.discount, orderQty);
  const relatedTier = getRelatedTier(product?.discount, orderQty, appliedTier);
  const percentBadge = getPercentBadge(product?.discount, appliedTier);

  const handleAddToCart = async () => {
    if (orderQty === 0) {
      setOpenError(true);
      return;
    }

    var body = {
      product_uuid: product.uuid,
      qty: orderQty,
    };

    try {
      await addToCart.mutateAsync(body);
      setOrderQty(0);
      setOpenSuccess(true);
    } catch (err) {
      if (isPbfRefusal(err)) setRefusal(err);
      else setErrorMessage(err.message.id ? err.message.id : err.message);
    }
  };

  return (
    <>
      <div className="p-2 rounded-lg bg-white text-[#2C3E5D] w-full max-w-[220px] overflow-hidden h-full self-stretch flex flex-col justify-between">
        <div className="mb-1">
          <div className="w-full flex justify-center items-center">
            {isLoadingCard ? (
              <Skeleton variant="rounded" width={"100%"} height={160} />
            ) : (
              <div className="w-full h-[160px] mb-2 relative">
                <img
                  src={product.images ? product.images[0] : ""}
                  alt={product.name}
                  className="object-contain w-full h-full"
                />
                {product.stock === 0 ? (
                  <div className="bg-[#FFE7E7] text-[#C62828] font-bold w-[80px] h-[80px] rounded-full absolute top-[40px] left-0 right-0 mx-auto flex justify-center items-center">
                    Habis
                  </div>
                ) : quotaEmpty ? (
                  <div
                    data-testid="quota-empty-badge"
                    className="bg-[#FFE7E7] text-[#C62828] font-bold text-center w-[80px] h-[80px] rounded-full absolute top-[40px] left-0 right-0 mx-auto flex justify-center items-center"
                  >
                    Kuota habis
                  </div>
                ) : null}
                {percentBadge ? (
                  <p className="absolute top-0 right-0 bg-red-100 text-red-500 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold">
                    {percentBadge}
                  </p>
                ) : null}
              </div>
            )}
          </div>
          {isLoadingCard ? null : (
            <div className="w-full">
              {/* Same place as the Android card: above the label, never under the price. */}
              {relatedTier ? (
                <div className="mb-1 h-[22px] flex items-center gap-1.5 bg-red-100 text-red-500 rounded-md px-2">
                  <FaTag className="shrink-0 text-[10px]" />
                  <p className="text-[10px] leading-snug font-medium truncate">
                    {getTierBarLabel(relatedTier)}
                  </p>
                </div>
              ) : (
                // keeps every card the same height inside the grid
                <div className="mb-1 h-[22px]" />
              )}
              {product.label ? (
                <p
                  className={`py-0.5 px-2 mb-1 rounded-full w-fit max-w-full truncate text-[10px] font-medium ${
                    SPECIAL_LABEL_STYLE[getSpecialLabelKey(product.label)]
                  }`}
                >
                  {product.label}
                </p>
              ) : null}
              <a
                href={`/purchase-pbf/detail-product/${product.slug}/${
                  type ? type : 1
                }`}
              >
                <div
                  className="text-[14px] font-semibold min-h-[2.75em] cursor-pointer hover:text-gray-300 leading-snug break-words overflow-hidden [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]"
                  title={product.name}
                >
                  {product.name}
                </div>
              </a>
              {/* Always one line tall, blank when unset, so cards stay aligned. */}
              <p
                className="text-[11px] text-gray-500 truncate min-h-[1.4em]"
                title={product.packaging}
              >
                {product.packaging}
              </p>
            </div>
          )}
        </div>

        {isLoadingCard ? (
          <div className="mb-2">
            <Skeleton variant="rounded" width={"100%"} height={20} />
          </div>
        ) : (
          <div className="mb-2">
            <p className="text-red-500 text-[14px] font-semibold">
              {getPriceLabel(
                product.base_price,
                product.discount,
                appliedTier,
                orderQty,
              )}
            </p>
            {/* Struck through only beside a discounted price or range, never the normal price. */}
            <p className="text-gray-500 text-[11px] min-h-[1.4em]">
              {percentBadge && (appliedTier || orderQty === 0) ? (
                <s>{toIDR(product.base_price)}</s>
              ) : null}
            </p>
            {quotaEmpty ? (
              <p className="text-red-500 text-[11px]">Kuota bulan ini habis</p>
            ) : quota ? (
              <p className="text-gray-500 text-[11px]">
                {quotaListLabel(quota)}
              </p>
            ) : null}
          </div>
        )}

        <div className="mb-2">
          <div className="flex items-center gap-2 w-full rounded-md border-2 p-2 bg-white w-fit">
            <button
              type="button"
              className={`text-[16px] hover:text-gray-300 text-gray-500 disabled:text-gray-300`}
              disabled={isLoadingCard || orderQty === 0 || maxQty === 0}
              onClick={() => setOrderQty(orderQty - 1)}
            >
              <FaMinus />
            </button>
            <input
              className="w-full text-center focus:outline-none"
              type="text"
              disabled={isLoadingCard || addToCart.isLoading || maxQty === 0}
              value={orderQty}
              onChange={(e) => {
                if (!isNaN(Number(e.target.value)))
                  setOrderQty(Math.min(Number(e.target.value), maxQty));
              }}
            />
            <button
              type="button"
              className={`text-[16px] hover:text-gray-300 text-gray-500 disabled:text-gray-300`}
              disabled={isLoadingCard || orderQty + 1 > maxQty || maxQty === 0}
              onClick={() => setOrderQty(Math.min(orderQty + 1, maxQty))}
            >
              <FaPlus />
            </button>
          </div>
          {quota && !quotaEmpty && orderQty === quota.remaining ? (
            <p className="text-gray-500 text-[11px] mt-1">
              {quotaCapHint(quota)}
            </p>
          ) : null}
        </div>

        <div className="mb-2">
          <button
            type="button"
            className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-full p-2 text-[14px] disabled:bg-gray-300"
            onClick={handleAddToCart}
            disabled={isLoadingCard || addToCart.isLoading || maxQty === 0}
          >
            {isLoadingCard ? (
              "Loading Product"
            ) : product.stock === 0 ? (
              "Habis"
            ) : quotaEmpty ? (
              "Kuota habis"
            ) : addToCart.isLoading || isFetchingCart ? (
              <CircularProgress size={10} />
            ) : (
              "+ Keranjang"
            )}
          </button>
        </div>
      </div>
      <SuccessAddCartPopup
        open={openSuccess}
        setOpen={setOpenSuccess}
        closeFunc={() => {
          setOrderQty(0);
        }}
      />
      <ErrorAddCartPopup open={openError} setOpen={setOpenError} />
      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <PbfRefusalDialog
        error={refusal}
        title="Tidak dapat ditambahkan"
        onClose={() => setRefusal(null)}
      />
    </>
  );
};

export default CardProduct;
